import { Box, Chip } from '@mui/material';
import {
  isAutogeneratedRow,
  type GridColDef,
  type GridInitialState,
  type GridSortModel,
} from '@mui/x-data-grid-premium';
import type { OrganizationGridRow } from './organizationsDataGridViewModel';

export const organizationGridInitialSortModel: GridSortModel = [
  { field: 'name', sort: 'asc' },
];

export const organizationGridExportFields = [
  'name',
  'description',
  'categoryLabels',
  'memberFamilyCount',
  'roleAssignmentCount',
] as const;

export const organizationGridInitialState: GridInitialState = {
  columns: { columnVisibilityModel: { organizationCount: false } },
  sorting: { sortModel: organizationGridInitialSortModel },
  pagination: { paginationModel: { pageSize: 25 } },
};

export const organizationGridFeatureProps = {
  disableAggregation: true,
  disableRowGrouping: true,
  disableRowSelectionOnClick: true,
} as const;

const reportingLockdown = {
  aggregable: false,
  chartable: false,
  groupable: false,
  pivotable: false,
};

const reportingDimension = {
  aggregable: false,
  chartable: true,
  groupable: false,
  pivotable: true,
};

const reportingMeasure = {
  aggregable: true,
  availableAggregationFunctions: ['sum'],
  chartable: true,
  groupable: false,
  pivotable: false,
};

export function canNavigateOrganizationRow(
  row: OrganizationGridRow,
  pivotActive: boolean
) {
  return !pivotActive && !isAutogeneratedRow(row);
}

export function categoryLabelsForDisplay(categoryLabels: string[]) {
  return categoryLabels.join(', ');
}

export function categoryLabelsForGrid(row: OrganizationGridRow) {
  if (isAutogeneratedRow(row)) return null;
  return categoryLabelsForDisplay(row.categoryLabels);
}

export function buildOrganizationsGridColumns({
  organizationCategoriesEnabled,
}: {
  organizationCategoriesEnabled: boolean;
}): GridColDef<OrganizationGridRow>[] {
  const columns: GridColDef<OrganizationGridRow>[] = [
    {
      ...reportingDimension,
      field: 'name',
      headerName: 'Name',
      minWidth: 200,
      flex: 0.8,
    },
    {
      ...reportingLockdown,
      field: 'description',
      headerName: 'Description',
      minWidth: 400,
      flex: 1.5,
    },
    {
      ...reportingMeasure,
      field: 'memberFamilyCount',
      headerName: 'Member Families',
      type: 'number',
      minWidth: 130,
      flex: 0.55,
      align: 'right',
      headerAlign: 'right',
    },
    {
      ...reportingMeasure,
      field: 'roleAssignmentCount',
      headerName: 'Role Assignments',
      type: 'number',
      minWidth: 140,
      flex: 0.6,
      align: 'right',
      headerAlign: 'right',
    },
    {
      ...reportingMeasure,
      field: 'organizationCount',
      headerName: 'Organization Count',
      type: 'number',
      disableExport: true,
      getApplyQuickFilterFn: () => null,
    },
  ];

  if (!organizationCategoriesEnabled) return columns;

  return [
    columns[0],
    columns[1],
    {
      ...reportingLockdown,
      field: 'categoryLabels',
      headerName: 'Categories',
      minWidth: 220,
      flex: 0.9,
      valueGetter: (_value, row) => categoryLabelsForGrid(row),
      valueFormatter: (value) => String(value ?? ''),
      renderCell: ({ row, value }) =>
        isAutogeneratedRow(row) ? (
          String(value ?? '')
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {row.categoryLabels.map((label, index) => (
              <Chip
                key={`${label}:${index}`}
                label={label}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        ),
    },
    columns[2],
    columns[3],
    columns[4],
  ];
}
